import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Minus, Plus } from 'lucide-react';
import GrantSpiralLavaLamp from './GrantSpiralLavaLamp';

/**
 * SpiralOrg Ultimate Landing Page
 * Anamorphic letterbox cinematic experience
 * Grant spiral at 30% opacity for organic dance effect
 * Minimal, elegant, verbatim copy from manifesto
 * Cinematic branding animations
 */
const SpiralOrgUltimateLanding = ({ onEnterProjects, onSwitchToAlternate }) => {
  const videoRef = useRef(null);
  const spiralTreeVideoRef = useRef(null);
  const secondVideoRef = useRef(null); // For the animal sounds video
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay
  const [showBranding, setShowBranding] = useState(false);
  const [showTaglines, setShowTaglines] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.8); // Dark overlay opacity (0.8 = 80% dark, fractal visible)

  useEffect(() => {
    // Start video MUTED for autoplay to work
    if (videoRef.current) {
      videoRef.current.muted = true; // Must start muted
      videoRef.current.play().then(() => {
        setVideoPlaying(true);
        console.log('✅ Video started (muted)');
      }).catch(e => {
        console.log('Autoplay blocked:', e);
      });
      
      // Unmute on first user interaction
      const unmute = () => {
        if (videoRef.current && isMuted) {
          videoRef.current.muted = false;
          setIsMuted(false);
          console.log('✅ Video unmuted on user interaction');
        }
      };
      
      // Listen for any user interaction
      document.addEventListener('click', unmute, { once: true });
      document.addEventListener('touchstart', unmute, { once: true });
      document.addEventListener('keydown', unmute, { once: true });
      
      // Sync text animations with video timeline - 3x longer persistence (20 seconds)
      const video = videoRef.current;
      
      const handleTimeUpdate = () => {
        const currentTime = video.currentTime;
        
        // Show branding at 4 seconds, persist until 24 seconds (20 seconds visible)
        if (currentTime >= 4 && currentTime < 24) {
          setShowBranding(true);
          setShowTaglines(true);
        } 
        // Hide text after 24 seconds
        else if (currentTime >= 24 || currentTime < 4) {
          setShowBranding(false);
          setShowTaglines(false);
        }
      };
      
      video.addEventListener('timeupdate', handleTimeUpdate);
      
      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, []);

  // Scroll animation effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollElements = document.querySelectorAll('.scroll-animate');
      scrollElements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.8;
        if (isVisible) {
          el.classList.add('animate-in');
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">
      
      {/* Vibrant Fractal Spiral Background - Giant Slow-Moving Orbs */}
      <div className="fixed inset-0 z-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_spiralorg/artifacts/022wg2ud_ChatGPT%20Image%20Oct%2011%2C%202025%20at%2012_38_50%20PM.jpeg"
          alt="Grant Spiral Fractal"
          className="w-full h-full object-cover"
          style={{ 
            filter: 'brightness(0.7) saturate(1.4) contrast(1.1)',
            mixBlendMode: 'screen'
          }}
        />
        {/* Adjustable dark overlay - default 80% to show fractal subtly */}
        <div 
          className="absolute inset-0 bg-black transition-opacity duration-500"
          style={{ opacity: backgroundOpacity }}
        ></div>
      </div>
      
      {/* Giant Lava Lamp Orbs - Slow In/Out Dimensional Movement */}
      <div className="fixed inset-0 z-[3] pointer-events-none" style={{ opacity: 0.5 }}>
        <GrantSpiralLavaLamp 
          size={4000}
          speed={0.3}
        />
      </div>
      
      {/* Steampunk Background Opacity Slider */}
      <div className="fixed bottom-6 left-6 z-50 bg-black/90 backdrop-blur-md border border-amber-700/60 rounded-xl p-4 shadow-2xl">
        <div className="flex items-center gap-3">
          <span className="text-xs text-amber-500 uppercase tracking-widest font-semibold" style={{ fontFamily: "'Cinzel', serif" }}>
            Fractal
          </span>
          <button
            onClick={() => setBackgroundOpacity(Math.min(1, backgroundOpacity + 0.1))}
            className="p-1.5 hover:bg-amber-700/30 rounded-lg transition-colors border border-amber-700/40"
            aria-label="Darken"
          >
            <Minus className="w-3 h-3 text-amber-500" />
          </button>
          <div className="w-20 h-1.5 bg-amber-950/70 rounded-full overflow-hidden border border-amber-800/50">
            <div 
              className="h-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all duration-300 shadow-lg shadow-amber-500/50"
              style={{ width: `${(1 - backgroundOpacity) * 100}%` }}
            ></div>
          </div>
          <button
            onClick={() => setBackgroundOpacity(Math.max(0.3, backgroundOpacity - 0.1))}
            className="p-1.5 hover:bg-amber-700/30 rounded-lg transition-colors border border-amber-700/40"
            aria-label="Lighten"
          >
            <Plus className="w-3 h-3 text-amber-500" />
          </button>
        </div>
      </div>

      {/* CSS for scroll animations */}
      <style>{`
        /* Glass Panel Styling - Ultra Premium */}
        .glass-panel {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.04));
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37), 
                      0 0 0 1px rgba(255, 255, 255, 0.05) inset,
                      0 20px 60px rgba(0, 0, 0, 0.5);
        }
        
        .glass-panel-dark {
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5));
          backdrop-filter: blur(25px) saturate(200%);
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 8px 40px 0 rgba(0, 0, 0, 0.6), 
                      0 0 0 1px rgba(255, 255, 255, 0.08) inset,
                      0 25px 80px rgba(0, 0, 0, 0.7);
        }
        
        .scroll-animate {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        
        .scroll-animate.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Epic Box Animations - Staggered BOOM BOOM BOOM effect */
        .epic-box-1 {
          opacity: 0;
          transform: translateY(50px) scale(0.95);
          transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .epic-box-2 {
          opacity: 0;
          transform: translateY(50px) scale(0.95);
          transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s;
        }
        
        .epic-box-3 {
          opacity: 0;
          transform: translateY(50px) scale(0.95);
          transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s;
        }
        
        .epic-box-1.animate-in,
        .epic-box-2.animate-in,
        .epic-box-3.animate-in {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        
        /* Mysterious sleek boxes for SpiralCred */
        .mystery-box {
          opacity: 0;
          transform: translateX(-30px);
          transition: all 0.8s ease-out;
        }
        
        .mystery-box.animate-in {
          opacity: 1;
          transform: translateX(0);
        }
        
        .mystery-box:hover {
          transform: translateX(5px) scale(1.02);
          box-shadow: 0 10px 40px rgba(255, 255, 255, 0.1);
        }
        
        .fade-in-brand {
          animation: dropAndShimmer 1.2s ease-out forwards;
        }
        
        .fade-in-taglines {
          animation: dropAndShimmer 1.5s ease-out 0.3s forwards;
        }
        
        .fade-out-brand {
          animation: fadeOut 2.5s ease-out forwards;
        }
        
        .fade-out-taglines {
          animation: fadeOut 2.5s ease-out forwards;
        }
        
        @keyframes dropAndShimmer {
          0% {
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
            filter: brightness(2) blur(10px);
          }
          50% {
            filter: brightness(1.5) blur(2px);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: brightness(1) blur(0);
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
            filter: brightness(1);
          }
          to {
            opacity: 0;
            filter: brightness(0.3);
          }
        }
        
        .shimmer-text {
          animation: shimmer 2s ease-in-out infinite;
        }
        
        @keyframes shimmer {
          0%, 100% {
            text-shadow: 0 0 10px rgba(255, 255, 255, 0.3), 
                         0 0 20px rgba(255, 255, 255, 0.2);
          }
          50% {
            text-shadow: 0 0 20px rgba(255, 255, 255, 0.6), 
                         0 0 40px rgba(255, 255, 255, 0.4),
                         0 0 60px rgba(230, 198, 133, 0.3);
          }
        }
      `}</style>

      {/* HERO - Anamorphic Letterbox with Video - BLACK CINEMATIC */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Black overlay ONLY for hero section to keep it cinematic */}
        <div className="absolute inset-0 bg-black z-[1]"></div>
        
        {/* Letterbox Container */}
        <div className="absolute inset-0 flex flex-col justify-center bg-transparent z-[2]">
          {/* Top Black Bar - Initially empty, branding fades in */}
          <div className="relative z-20 bg-black py-8 px-6">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              {showBranding && (
                <h1 
                  className={`text-5xl md:text-6xl font-light tracking-widest shimmer-text ${showBranding ? 'fade-in-brand' : 'fade-out-brand'}`}
                  style={{ 
                    fontFamily: "'Cinzel', serif",
                    letterSpacing: '0.35em'
                  }}
                >
                  SpiralOrg
                </h1>
              )}
              
              {/* Mute Button with Click for Sound Hint */}
              <div className="flex items-center gap-3 ml-auto">
                {isMuted && (
                  <span className="text-xs text-white/50 animate-pulse" style={{ fontFamily: "'Cinzel', serif" }}>
                    Click for sound
                  </span>
                )}
                <button
                  onClick={toggleMute}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? (
                    <VolumeX className="w-4 h-4 text-white/60" />
                  ) : (
                    <Volume2 className="w-4 h-4 text-white/60" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Video - Anamorphic 2.39:1 aspect ratio - Letterbox on ALL screens including mobile */}
          <div className="relative z-10 w-full" style={{ aspectRatio: '2.39 / 1', maxHeight: '60vh' }}>
            <video 
              ref={videoRef}
              autoPlay 
              loop 
              playsInline
              className="w-full h-full object-cover"
              style={{ 
                filter: 'brightness(0.75) contrast(1.15) saturate(1.1)',
                objectPosition: 'center center'
              }}
            >
              <source src="https://customer-assets.emergentagent.com/job_spiralorg/artifacts/hujg7xrk_SP.mp4" type="video/mp4" />
            </video>
          </div>

          {/* Bottom Black Bar - Cinematic taglines fade in */}
          <div className="relative z-20 bg-black py-12 px-6">
            <div className="max-w-7xl mx-auto text-center">
              {showTaglines && (
                <div className={`space-y-3 ${showTaglines ? 'fade-in-taglines' : 'fade-out-taglines'}`}>
                  <p 
                    className="text-xl md:text-2xl font-light text-white/90 shimmer-text"
                    style={{ 
                      fontFamily: "'Cormorant Garamond', serif",
                      letterSpacing: '0.25em'
                    }}
                  >
                    spiral organization
                  </p>
                  <p 
                    className="text-xl md:text-2xl font-light text-white/80 shimmer-text"
                    style={{ 
                      fontFamily: "'Cormorant Garamond', serif",
                      letterSpacing: '0.25em'
                    }}
                  >
                    spiral organizer
                  </p>
                  <p 
                    className="text-xl md:text-2xl font-light text-white/70 shimmer-text"
                    style={{ 
                      fontFamily: "'Cormorant Garamond', serif",
                      letterSpacing: '0.25em'
                    }}
                  >
                    spiral organism
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <div className="relative z-10">
        
        {/* TOP LEVEL COPY - Introduction */}
        <section className="relative py-24 px-6 bg-black/95">
          <div className="max-w-5xl mx-auto text-center scroll-animate">
            <h2 
              className="text-4xl md:text-5xl font-light text-white mb-8 leading-tight"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
            >
              A Guild of Families Building Belonging<br/>
              Through Logistics <span className="italic text-5xl md:text-6xl">and</span> Song.
            </h2>
            
            <div className="h-px bg-white/20 my-12 max-w-md mx-auto"></div>
            
            <p 
              className="text-xl md:text-2xl leading-relaxed text-white/90 mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              An old-school spiral organizer of people and projects, activating bliss ventures, retreats, missions and purpose — the way nature does — in organic spirals of interconnection, coherence and confluence.
            </p>
            
            <p 
              className="text-lg md:text-xl text-white/80 leading-relaxed mb-12 italic"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              It's part digital tool, part human federation:<br/>
              a planner, a mentor alliance, and a movement all in one.
            </p>
            
            <h3 
              className="text-3xl md:text-4xl font-light text-white mt-16"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
            >
              How Can SpiralOrg Help Activate Your Org?
            </h3>
          </div>
        </section>
        
        {/* EPIC THREE LAYERS - Organization, Organizer, Organism */}
        <section className="relative py-32 px-6 bg-black/95">
          <div className="max-w-7xl mx-auto">
            <h2 
              className="text-5xl md:text-6xl font-light text-white mb-20 text-center scroll-animate"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.15em' }}
            >
              The Three Layers
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Box 1 - Spiral Organization */}
              <div className="epic-box-1 scroll-animate bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border-2 border-white/30 rounded-3xl p-10 hover:border-white/60 hover:shadow-2xl transition-all duration-300">
                <div className="text-7xl mb-6 text-center">🏛️</div>
                <h3 
                  className="text-3xl font-bold text-white mb-6 text-center"
                  style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
                >
                  Spiral Organization
                </h3>
                <div className="h-px bg-white/30 mb-6"></div>
                <p className="text-lg leading-relaxed text-white/90 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  The lawful <span className="text-white font-semibold">Chapter 184</span> unincorporated nonprofit association.
                </p>
                <p className="text-base leading-relaxed text-white/80 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Essentially the same functionality as any registered entity, but ours is <span className="italic">unregistered</span>, <span className="italic">private</span>, and <span className="font-semibold text-white">owned by the families</span>.
                </p>
                <p className="text-sm text-white/70 italic mt-6">
                  Legal, sovereign, and family-controlled.
                </p>
              </div>
              
              {/* Box 2 - Spiral Organizer */}
              <div className="epic-box-2 scroll-animate bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-sm border-2 border-white/40 rounded-3xl p-10 hover:border-white/70 hover:shadow-2xl transition-all duration-300" style={{ boxShadow: '0 20px 60px rgba(255, 255, 255, 0.1)' }}>
                <div className="text-7xl mb-6 text-center">⚙️</div>
                <h3 
                  className="text-3xl font-bold text-white mb-6 text-center"
                  style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
                >
                  Spiral Organizer
                </h3>
                <div className="h-px bg-white/30 mb-6"></div>
                <p className="text-lg leading-relaxed text-white/90 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Truly <span className="font-semibold text-white">amazing, simple tools</span> that help families plan life's craziest events.
                </p>
                <p className="text-base leading-relaxed text-white/80 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Processes that remedy conflict and help us get things done.
                </p>
                <div className="bg-white/5 rounded-xl p-4 mt-6">
                  <p className="text-sm text-white/90">
                    ✓ Family Project Management<br/>
                    ✓ Conflict Remedy Processes<br/>
                    ✓ Calendar & Task Coordination<br/>
                    ✓ Trust Ledger & Credit System
                  </p>
                </div>
              </div>
              
              {/* Box 3 - Spiral Organism */}
              <div className="epic-box-3 scroll-animate bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border-2 border-white/30 rounded-3xl p-10 hover:border-white/60 hover:shadow-2xl transition-all duration-300">
                <div className="text-7xl mb-6 text-center">🌱</div>
                <h3 
                  className="text-3xl font-bold text-white mb-6 text-center"
                  style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
                >
                  Spiral Organism
                </h3>
                <div className="h-px bg-white/30 mb-6"></div>
                <p className="text-lg leading-relaxed text-white/90 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  The spiral interconnected harmonic energies of the people of Earth.
                </p>
                <p className="text-base leading-relaxed text-white/80" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  <span className="font-semibold text-white">Banding together to reclaim our agency.</span>
                </p>
                <p className="text-sm text-white/70 italic mt-6">
                  The living network of consciousness and collaboration.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* THE PROMISE */}
        <section className="relative py-24 px-6 bg-gradient-to-b from-black/95 to-black/90">
          <div className="max-w-5xl mx-auto">
            <div className="glass-panel rounded-3xl p-12 scroll-animate">
              <h2 
                className="text-4xl md:text-5xl font-light text-white mb-12 text-center"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
              >
                The Promise
              </h2>
              
              <p 
                className="text-2xl leading-relaxed text-white/90 mb-8 text-center"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Every spiral intersects another. Every intersection births light. Each act of reunion—digital or physical—is a micro-creation of the world we keep saying we want.
              </p>

              <p 
                className="text-xl leading-relaxed text-white mb-12"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                SpiralOrg reminds us we are not a machine civilization; we're a <span className="font-semibold text-white">family civilization</span>. Each Library is a proof. Each project is a physics experiment in belonging. Each breath we share is the universe remembering how to be whole.
              </p>
              
              <div className="border-t border-white/20 pt-8 mt-8">
                <p 
                  className="text-base uppercase tracking-widest text-white mb-4 text-center"
                  style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.3em' }}
                >
                  With belonging, we get things done
                </p>
                <p 
                  className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  We hyper-thrive for 10,000 generations.
                </p>
              </div>

              <div className="bg-black/40 rounded-2xl p-8 mb-8">
                <p className="text-xl leading-relaxed text-white/90 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  We are a <span className="text-white font-semibold">Private Membership Association</span> operating under Chapter 184 of Wisconsin law—a benevolent, lawful network formed for mutual benefit.
                </p>

                <p className="text-lg leading-relaxed text-white/80 mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Free to organize, create, and trade family to family, project to project, heart to heart. You belong as long as you agree to our tenets:
                </p>

                <p 
                  className="text-4xl mb-6 text-center"
                  style={{ 
                    fontFamily: "'Cinzel', serif",
                    color: 'white',
                    letterSpacing: '0.3em',
                    fontWeight: '300'
                  }}
                >
                  AGENCY · SECURITY · TRUST
                </p>

                <p className="text-lg text-white/80 italic text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  And the deeper purpose: the procreation of love and wisdom for 10,000 generations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* AN EXAMPLE OF HOW THIS HELPS */}
        <section className="relative py-24 px-6 bg-black/90">
          <div className="max-w-5xl mx-auto scroll-animate">
            <h2 
              className="text-4xl md:text-5xl font-light text-white mb-12 text-center"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
            >
              An Example of How This Helps
            </h2>
            
            <div className="bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm border border-white/10 rounded-3xl p-12">
              <p 
                className="text-lg leading-relaxed text-white/90 mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                A daughter wanted to celebrate her aging parent's milestone birthday without turning love into logistical chaos. Using SpiralOrg, the extended family coordinated a small reunion—framed as a celebration, engineered as reconnection. One cousin brought music, others managed food and travel, and the family held a circle where each generation shared gratitude and voiced one concrete need.
              </p>

              <div className="bg-black/40 rounded-2xl p-6 my-8 border border-white/20">
                <h4 className="text-white font-bold mb-4 text-lg" style={{ fontFamily: "'Cinzel', serif" }}>
                  The entire event ran through the SpiralOrg toolkit:
                </h4>
                <div className="space-y-3 text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  <div className="flex items-start gap-3">
                    <span className="text-white text-xl">✓</span>
                    <span>Invites, carpools, budgets, task assignments, and a simple "thank-you ledger" lived in one shared space.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-white text-xl">✓</span>
                    <span>Photos, recipes, stories, and family memories flowed straight into the <span className="font-semibold text-white">Lore Library</span> for future reference.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-white text-xl">✓</span>
                    <span>Every small contribution was tracked as a micro-win—creating patterns they could repeat regularly, not just once a year.</span>
                  </div>
                </div>
              </div>

              <p 
                className="text-lg leading-relaxed text-white italic mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                After the gathering, everything lived on as a Family Project: a clear record of who contributed what, who needed support, and memories that proved it mattered. Stress dropped. Participation increased. People felt valued. Belonging lasted beyond the event itself.
              </p>
              
              <p 
                className="text-xl leading-relaxed font-bold text-white text-center"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                This is how family reunions become engines of belonging.
              </p>
            </div>
          </div>
        </section>
        
        {/* MONEY IS AN ILLUSION - Realization Moment */}
        <section className="relative py-24 px-6 bg-gradient-to-b from-black/90 to-black/95">
          <div className="max-w-4xl mx-auto text-center scroll-animate">
            <p 
              className="text-2xl md:text-3xl leading-relaxed text-white/90 mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              There's a moment when the noise finally fades and you see it:<br/>
              <span className="text-white font-medium italic text-3xl md:text-4xl">money was always a story.</span>
            </p>

            <div className="h-px bg-white/20 my-12 max-w-md mx-auto"></div>

            <p 
              className="text-lg text-white/80 leading-relaxed"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              A shimmering agreement we all kept repeating because we were afraid not to.
            </p>
          </div>
        </section>
        
        {/* 2. The Great Realization */}
        <section className="relative py-24 px-6 bg-gradient-to-b from-black/95 to-black/90">
          <div className="max-w-4xl mx-auto text-center scroll-animate">
            <h2 
              className="text-3xl md:text-4xl font-light text-white mb-12"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
            >
              The Great Realization
            </h2>
            
            <p 
              className="text-xl leading-relaxed text-white/90 mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Everything is made of intersecting spirals of energy —<br/>
              the breath of galaxies, the curve of shells, the rhythm between two hearts.<br/>
              Where these spirals meet in harmony, reality holds.<br/>
              When they fall out of tune, things break.
            </p>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 my-12">
              <p 
                className="text-lg text-white/80 leading-relaxed"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                The goldsmiths and lenders understood this in their way.<br/>
                They tuned the spiral to themselves.<br/>
                Now the harmony is fading, and the question remains:<br/>
                <span className="text-white text-xl">Who will remember how to sing?</span>
              </p>
            </div>
          </div>
        </section>

        {/* 3. The Unless */}
        <section className="relative py-24 px-6 bg-black/90">
          <div className="max-w-4xl mx-auto scroll-animate">
            <div className="bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm border border-white/10 rounded-3xl p-12">
              <h2 
                className="text-3xl md:text-4xl font-light text-white mb-8 text-center"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
              >
                The Unless
              </h2>
              
              <p 
                className="text-xl leading-relaxed text-white/90 mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                The game is rigged, yes.<br/>
                Credit created from nothing, collapse timed like the tides.
              </p>

              <p 
                className="text-2xl leading-relaxed text-white mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                But <span className="text-3xl font-medium">unless</span> — unless we create our own credit.
              </p>

              <div className="space-y-3 pl-6 border-l-2 border-white/30">
                <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Person to person.
                </p>
                <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Interest-free.
                </p>
                <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Based entirely on trust.
                </p>
              </div>

              <div className="h-px bg-white/20 my-8"></div>

              <p 
                className="text-lg text-white/80 leading-relaxed"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                A voluntary exchange between members.<br/>
                A quiet ledger of belief in one another.<br/>
                No interest, no penalty — only record of gratitude.<br/>
                <span className="text-white italic">A small thing maybe, but small things move worlds.</span>
              </p>
            </div>
          </div>
        </section>

        {/* 4. SpiralCred - Mysterious Sleek Design */}
        <section className="relative py-32 px-6 bg-gradient-to-b from-black/90 to-black">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 scroll-animate">
              <h2 
                className="text-5xl md:text-6xl font-light text-white mb-6"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.2em' }}
              >
                SpiralCred
              </h2>
              <p 
                className="text-2xl text-white/80 mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Credit · Credentials · Credibility
              </p>
              <p 
                className="text-lg text-white/60 italic"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Like the banks, we grant each other credit
              </p>
            </div>

            {/* SpiralCred Visual */}
            <div className="mb-16 scroll-animate">
              <img 
                src="https://customer-assets.emergentagent.com/job_spiralorg/artifacts/vxq5ntr6_55961C47-FD3B-4504-A6DC-63FAFBD69702.png"
                alt="SpiralCred - The Physics of Trust"
                className="w-full max-w-4xl mx-auto rounded-xl border border-white/20"
                style={{ boxShadow: '0 20px 80px rgba(0, 0, 0, 0.8)' }}
              />
            </div>

            {/* Mysterious Sleek Boxes */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="mystery-box scroll-animate bg-black/80 backdrop-blur-sm border border-white/20 rounded-2xl p-8 hover:bg-black/60 transition-all">
                <h4 className="text-white font-semibold text-xl mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                  Bolstered by Trust
                </h4>
                <p className="text-white/80 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  The more you're trusted, the more <span className="text-white font-medium">trust bumps</span> you get.<br/>
                  A little bit more credit granted by the community.
                </p>
              </div>

              <div className="mystery-box scroll-animate bg-black/80 backdrop-blur-sm border border-white/20 rounded-2xl p-8 hover:bg-black/60 transition-all" style={{ transitionDelay: '0.1s' }}>
                <h4 className="text-white font-semibold text-xl mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                  Standard Exchange Plus Trust
                </h4>
                <p className="text-white/80 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  In addition to normal ways of exchange, <span className="text-white font-medium">SpiralCred circulates within the family economy</span>.
                </p>
              </div>

              <div className="mystery-box scroll-animate bg-black/80 backdrop-blur-sm border border-white/20 rounded-2xl p-8 hover:bg-black/60 transition-all" style={{ transitionDelay: '0.2s' }}>
                <h4 className="text-white font-semibold text-xl mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                  Credit = Trust Made Visible
                </h4>
                <p className="text-white/80 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Each act of generosity adds a ripple.<br/>
                  Not wealth—warmth made visible.
                </p>
              </div>

              <div className="mystery-box scroll-animate bg-black/80 backdrop-blur-sm border border-white/20 rounded-2xl p-8 hover:bg-black/60 transition-all" style={{ transitionDelay: '0.3s' }}>
                <h4 className="text-white font-semibold text-xl mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                  Credentials & Credibility
                </h4>
                <p className="text-white/80 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Your reputation, your skills, your contributions—<br/>
                  all woven into your spiral standing.
                </p>
              </div>
            </div>

            {/* Core Message */}
            <div className="bg-gradient-to-r from-black/90 to-black/70 backdrop-blur-xl border-2 border-white/20 rounded-3xl p-12 text-center scroll-animate">
              <p 
                className="text-2xl md:text-3xl leading-relaxed text-white mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                No fees. No usury. Just belonging.
              </p>
              <div className="h-px bg-white/20 my-6 max-w-md mx-auto"></div>
              <p 
                className="text-xl text-white/80 italic"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                If they take the money away, we'll laugh gently,<br/>
                because <span className="text-white font-medium not-italic">we are the bank</span>.
              </p>
            </div>
          </div>
        </section>

        {/* 5. What Is Rich? */}
        <section className="relative py-24 px-6 bg-black/95">
          <div className="max-w-4xl mx-auto text-center scroll-animate">
            <h2 
              className="text-3xl md:text-4xl font-light text-white mb-12"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
            >
              What Is Rich?
            </h2>
            
            <p 
              className="text-xl leading-relaxed text-white mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Rich is what you carry when there's nothing left to sell —<br/>
              the craft your ancestors taught you,<br/>
              the stories that outlived empires,<br/>
              the song you hum when you're afraid and someone else joins in.
            </p>

            <div className="inline-block bg-gradient-to-br from-white/10 to-transparent backdrop-blur-sm border border-white/20 rounded-2xl p-8 my-8">
              <p 
                className="text-4xl text-white font-light mb-2"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.2em' }}
              >
                Ritche
              </p>
              <p 
                className="text-sm text-white/70"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                The mastery, the passion, the authority of experience.<br/>
                The inheritance that cannot be repossessed.
              </p>
            </div>

            <p 
              className="text-lg text-white/80 leading-relaxed"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              No more forgetting.<br/>
              No more surrendering the wisdom of our fathers' fathers and mothers' mothers.<br/>
              <span className="text-white text-xl">We remember.</span>
            </p>
          </div>
        </section>

        {/* 6. Re-Creation Through Reunion */}
        <section className="relative py-24 px-6 bg-gradient-to-b from-black/95 to-black/90">
          <div className="max-w-5xl mx-auto scroll-animate">
            <h2 
              className="text-3xl md:text-4xl font-light text-white mb-12 text-center"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
            >
              Re-Creation Through Reunion
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                <p 
                  className="text-lg leading-relaxed text-white"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  So we gather.<br/>
                  We sing.<br/>
                  We host small reunions in barns and backyards and forgotten halls.<br/>
                  We disguise harmony as a family reunion —<br/>
                  because Re-Union sounds ordinary enough to pass the censors of the old world.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                <p 
                  className="text-lg leading-relaxed text-white"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Reunion becomes re-creation.<br/>
                  Re-creation becomes innovation.<br/>
                  Each family helping another.<br/>
                  Each circle of trust strengthening the next.<br/>
                  <br/>
                  <span className="text-white/80 italic">Never perfect, never complete — just endlessly rotating,<br/>
                  spiral-reciprocating energies.</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Visual Interlude - New Video with Barely Perceptible Audio */}
        <section className="relative py-16 px-6 overflow-hidden bg-black">
          <div className="max-w-6xl mx-auto">
            <div className="scroll-animate">
              <video 
                ref={secondVideoRef}
                autoPlay 
                loop 
                muted={false}
                playsInline
                className="w-full rounded-2xl border border-white/20 shadow-2xl"
                style={{ 
                  filter: 'brightness(0.9) contrast(1.1)',
                  maxHeight: '70vh',
                  objectFit: 'cover'
                }}
                onLoadedMetadata={(e) => {
                  e.target.volume = 0.03; // Barely perceptible - 3% volume
                  console.log('✅ Second video volume set to 0.03');
                }}
              >
                <source src="https://customer-assets.emergentagent.com/job_spiralorg/artifacts/69jolnt9_Video_Generation_With_Sound.mov" type="video/mp4" />
              </video>
            </div>
          </div>
        </section>

        {/* 7. SpiralOrg - The Living Organizer */}
        <section className="relative py-24 px-6 bg-black/90">
          <div className="max-w-4xl mx-auto text-center scroll-animate">
            <h2 
              className="text-3xl md:text-4xl font-light text-white mb-12"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
            >
              SpiralOrg · The Living Organizer
            </h2>
            
            <div className="bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm border border-white/10 rounded-3xl p-12">
              <p 
                className="text-xl leading-relaxed text-white mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Not an institution, but a living organism —<br/>
                a spiral organizer of clans, builders, healers, makers, and mothers.<br/>
                Transactions recorded as gratitude, not extraction.<br/>
                A ledger of love written in light.
              </p>

              <div className="h-px bg-white/20 my-8"></div>

              <p 
                className="text-lg text-white/80 italic"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                The bankers will call it impossible.<br/>
                Let them.<br/>
                <span className="text-white not-italic font-medium text-xl">We are tired of impossible things being the only things that exist.</span>
              </p>
            </div>
          </div>
        </section>

        {/* 8. The Physics of Hope */}
        <section className="relative py-24 px-6 bg-gradient-to-b from-black/90 to-black/95">
          <div className="max-w-4xl mx-auto text-center scroll-animate">
            <h2 
              className="text-3xl md:text-4xl font-light text-white mb-12"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
            >
              The Physics of Hope
            </h2>
            
            <p 
              className="text-2xl leading-relaxed text-white mb-12"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Natural law still loves us.<br/>
              Entropy flows, but intention can lens it.<br/>
              The spiral endures — the geometry of grace,<br/>
              the mechanism of everything that still works.
            </p>

            <p 
              className="text-xl text-white/90 italic"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              We may not succeed.<br/>
              But imagine the beauty if we do.
            </p>
          </div>
        </section>

        {/* 9. The Simple Realization */}
        <section className="relative py-24 px-6 bg-black/95">
          <div className="max-w-5xl mx-auto scroll-animate">
            <h2 
              className="text-3xl md:text-4xl font-light text-white mb-12 text-center"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
            >
              The Simple Realization
            </h2>
            
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-12">
              <p 
                className="text-xl leading-relaxed text-white mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                After all of this, the truth is tenderly simple.<br/>
                The world only wants to live in balance.<br/>
                Every tide, every forest, every heartbeat seeks homeostasis —<br/>
                the quiet equilibrium where life protects life.
              </p>

              <div className="bg-white/5 rounded-2xl p-8 my-8">
                <p 
                  className="text-lg leading-relaxed text-white"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  The martial forces protect the maternal with their lives.<br/>
                  That's the whole equation.<br/>
                  That's the ancient purpose of the brave:<br/>
                  to guard the mother,<br/>
                  to safeguard the procreation of love and wisdom for ten thousand generations.
                </p>
              </div>

              <p 
                className="text-lg leading-relaxed text-white/90 mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                The mother creates the world worth guarding;<br/>
                the brave defend the love that keeps it turning.<br/>
                Between them, the song continues.
              </p>

              <div className="h-px bg-white/20 my-8"></div>

              <p 
                className="text-lg leading-relaxed text-white/80"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                If we can remember to sing instead of shout,<br/>
                to adapt with the genius that made us human in the first place,<br/>
                we can quietly reclaim our agency as a species —<br/>
                not through conquest, but through coherence.
              </p>

              <p 
                className="text-xl text-white mt-8 text-center"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                A fractal connection of families,<br/>
                bound by trust,<br/>
                singing the old song in a new key.
              </p>

              <p 
                className="text-lg text-white/80 italic text-center mt-8"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                The world is already trying to heal.<br/>
                We're just here to help it remember the tune.
              </p>
            </div>
          </div>
        </section>

        {/* Spiral Tree Video Section - Original Landing Page Video */}
        <section className="relative py-24 px-6 overflow-hidden bg-black">
          <div className="absolute inset-0 z-0">
            <video 
              ref={spiralTreeVideoRef}
              autoPlay 
              loop 
              muted 
              playsInline
              className="w-full h-full object-cover opacity-40"
              style={{ 
                filter: 'brightness(0.6) contrast(1.2)',
                transform: 'scale(1.2)'
              }}
            >
              <source src="/spiral-tree-animation.mov" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80"></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto text-center scroll-animate">
            <h2 
              className="text-4xl md:text-5xl font-light text-white mb-8"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.15em' }}
            >
              A Living Spiral
            </h2>
            <p 
              className="text-2xl text-white/90 leading-relaxed"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Every spiral intersects another.<br/>
              Every intersection births light.<br/>
              Each act of reunion—digital or physical—<br/>
              is a micro-creation of the world we keep saying we want.
            </p>
          </div>
        </section>

        {/* 10. The Invitation */}
        <section className="relative py-32 px-6 bg-gradient-to-b from-black/95 to-black">
          <div className="max-w-5xl mx-auto scroll-animate">
            <div className="text-center mb-16">
              <h2 
                className="text-5xl md:text-6xl font-light text-white mb-8"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
              >
                The Invitation
              </h2>
              
              <p 
                className="text-2xl leading-relaxed text-white mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Join the Spiral.<br/>
                Build the trust.<br/>
                Hold the line of love.
              </p>

              <div className="h-px bg-white/20 my-12 max-w-md mx-auto"></div>

              <p 
                className="text-xl text-white/80 italic"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Because if we succeed, even a little,<br/>
                what an incredible way to fail.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
              <button
                onClick={onEnterProjects}
                className="px-10 py-4 bg-white/10 hover:bg-white/20 border border-white/30 hover:border-white/50 text-white rounded-lg text-lg transition-all"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
              >
                Learn About SpiralCred
              </button>

              <button
                onClick={onEnterProjects}
                className="px-10 py-4 bg-white/10 hover:bg-white/20 border border-white/30 hover:border-white/50 text-white rounded-lg text-lg transition-all"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
              >
                Join the Federation of Families
              </button>
            </div>
          </div>
        </section>

        {/* REMEMBER - The Core Truth (Verbatim) */}
        <section className="relative py-32 px-6 bg-black/40">
          <div className="max-w-6xl mx-auto scroll-animate">
            <div className="glass-panel-dark rounded-3xl p-12 md:p-16">
              <h2 
                className="text-5xl md:text-6xl font-light text-white mb-12 text-center"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.15em' }}
              >
                Remember
              </h2>
              
              <div className="space-y-8 text-lg md:text-xl leading-relaxed text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                <p>
                  If we can lightly join together under a common banner, with a common set of wisdom, ensure it remains family, founder owned, make it not about the org, but about <span className="font-semibold text-white">the organism that is all the people families that make up the whole</span>, by banding together, and realizing that we can create our own credit, lawfully in a practical easy to use ledger between family to family member to member combined with an incredible technology that helps heal families, almost instantaneously, now unburdened by the bondage of the old ways that we essentially worked to maintain the illusion of the billionaires ledger, and the coupled, unnecessary family, Stripe, energy sync, we rise to become <span className="font-bold text-2xl text-white">the brave and the fearless mothers!!</span>
                </p>

                <div className="h-px bg-white/20 my-8"></div>

                <p>
                  And from there, we are provided with tools that make family and team project planning dreaming execution, not only not a pain, but a <span className="font-semibold text-white">joy</span>. Healing activation, elucidation, execution, joyful administration, Sustainable.
                </p>

                <p>
                  For families connected together around the world, and all we need to do to accomplish this is to <span className="font-bold text-2xl text-white">remember. Remember remember remember!</span>
                </p>

                <div className="glass-panel rounded-2xl p-8 my-8">
                  <p className="text-xl md:text-2xl text-center">
                    We have done this for longer than we have endure the bondage of the billionaire class families that have ruled us for the last 10,000 for the last no more.
                  </p>
                </div>

                <p className="text-center text-2xl md:text-3xl font-medium">
                  For now do not rise per se Simply push shroud aside to the side and see that it has been. <span className="font-bold text-white">We have had the limitless energy, a.k.a. money the whole time!!</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* BY INVITATION ONLY + Beta Test Access */}
        <section className="relative py-24 px-6 bg-gradient-to-b from-black/40 to-black/60">
          <div className="max-w-5xl mx-auto scroll-animate">
            <div className="glass-panel rounded-3xl p-12 text-center">
              <h2 
                className="text-4xl md:text-5xl font-light text-white mb-6"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.15em' }}
              >
                By Invitation Only
              </h2>
              
              <p className="text-xl text-white/90 mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                SpiralOrg is a private membership for families ready to reclaim their sovereignty.
              </p>

              <div className="h-px bg-white/20 my-8 max-w-md mx-auto"></div>

              <div className="glass-panel-dark rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-semibold text-white mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                  Beta Test the Family Project Organizer
                </h3>
                <p className="text-white/80 mb-6 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  <span className="italic">Work in progress</span> — Our goal: a crowd-sourced, epic, universal spiral organizer for the modern age to help families <span className="font-semibold text-white">hyper-thrive</span>.
                </p>
                <p className="text-lg text-white mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  This is first and foremost about helping <span className="font-bold text-white text-xl">YOUR family hyper-thrive!</span>
                </p>
                <button
                  onClick={onEnterProjects}
                  className="px-10 py-4 bg-gradient-to-r from-white/20 to-white/10 hover:from-white/30 hover:to-white/20 border-2 border-white/40 hover:border-white/60 text-white rounded-xl text-lg font-semibold transition-all shadow-2xl"
                  style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
                >
                  Enter Beta Test →
                </button>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                <p className="text-base text-white/80 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  When families hyper-thrive through <span className="font-semibold text-white">Spiral Council remediation</span> of unnecessary suffering through unresolved conflict, then the whole family thrives—
                </p>
                <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Cinzel', serif" }}>
                  the whole planetary family!!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Signature Scroll */}
        <section className="relative py-16 px-6 bg-black border-t border-white/10">
          <div className="max-w-4xl mx-auto text-center">
            <p 
              className="text-lg text-white/70 leading-relaxed mb-12"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              This is the living manifesto of a world finding its balance again.<br/>
              The adventure is real. The field is open. We launch now.
            </p>

            {/* Planetary Fed Links */}
            <div className="flex flex-wrap justify-center gap-6 mb-8 text-sm text-white/50">
              <a href="#" className="hover:text-white/80 transition-colors">seedypeeps.com</a>
              <a href="#" className="hover:text-white/80 transition-colors">planetaryfed.com</a>
              <a href="#" className="hover:text-white/80 transition-colors">trustwoven.com</a>
              <a href="#" className="hover:text-white/80 transition-colors">re-inn.com</a>
              <a href="#" className="hover:text-white/80 transition-colors">ritesofpresence.com</a>
            </div>

            <p className="text-white/40 text-sm mb-8">
              SpiralOrg · Chapter 184 Wisconsin<br/>
              Operating as a Private Membership Association for the mutual benefit of its members.<br/>
              © Planetary Federation of Families 2025
            </p>

            {/* Switch to Alternative Version */}
            {onSwitchToAlternate && (
              <div className="pt-8 border-t border-white/10">
                <button
                  onClick={onSwitchToAlternate}
                  className="text-white/60 hover:text-white text-sm transition-colors"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  View Alternative Landing Page →
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SpiralOrgUltimateLanding;
